import { 
  setupAuth, 
  setupRequestLogging, 
  setupErrorTracking
} from '../api/setup';
import { monitoringService, apiClient, telemetryService } from '../api';
import { createContextLogger } from '../utils/logger';

const logger = createContextLogger('ApiConfig');

export interface ApiConfig {
  enableLogging?: boolean;
  enableMonitoring?: boolean;
  enableErrorTracking?: boolean;
  enableTelemetry?: boolean;
  authToken?: string;
  environment?: 'development' | 'staging' | 'production';
  customErrorHandler?: (error: any) => void;
  telemetry?: {
    serviceName?: string;
    serviceVersion?: string;
    endpoint?: string;
    enableConsoleExporter?: boolean;
    enableAutoInstrumentation?: boolean;
  };
}

/**
 * Initialize API layer with configuration
 */
export const initializeApi = (config: ApiConfig = {}) => {
  const {
    enableLogging = process.env.NODE_ENV === 'development',
    enableMonitoring = true,
    enableErrorTracking = true,
    enableTelemetry = process.env.NODE_ENV !== 'production', // Enable in dev/staging
    authToken,
    environment = process.env.NODE_ENV as 'development' | 'staging' | 'production' || 'development',
    customErrorHandler,
    telemetry = {}
  } = config;

  logger.info('Initializing API layer...', {
    enableLogging,
    enableMonitoring,
    enableErrorTracking,
    enableTelemetry,
    environment,
    hasAuthToken: !!authToken,
  });

  // Set up authentication if token provided
  if (authToken) {
    setupAuth(authToken);
    logger.debug('Authentication configured');
  }

  // Set up request/response logging in development
  if (enableLogging) {
    setupRequestLogging();
    logger.debug('Request logging enabled');
  }

  // Set up error tracking
  if (enableErrorTracking) {
    if (customErrorHandler) {
      setupErrorTracking(customErrorHandler);
      logger.debug('Custom error tracking enabled');
    } else {
      // Default error tracking
      setupErrorTracking((error) => {
        logger.error('API Error tracked:', error);
        
        // Track error in telemetry if enabled
        if (enableTelemetry) {
          telemetryService.trackError(error, 'API');
        }
        
        // In production, you might want to send to external service
        if (environment === 'production') {
          // Example: Send to error tracking service
          // Sentry.captureException(error);
          // LogRocket.captureException(error);
        }
      });
      logger.debug('Default error tracking enabled');
    }
  }

  // Set up OpenTelemetry
  if (enableTelemetry) {
    try {
      telemetryService.initialize({
        serviceName: telemetry.serviceName || 'hop-studio-frontend',
        serviceVersion: telemetry.serviceVersion || '1.0.0',
        environment,
        endpoint: telemetry.endpoint, // Optional OTLP endpoint
        enableConsoleExporter: telemetry.enableConsoleExporter ?? (environment === 'development'),
        enableAutoInstrumentation: telemetry.enableAutoInstrumentation ?? true,
      });
      logger.debug('OpenTelemetry enabled');
    } catch (error) {
      logger.error('Failed to initialize OpenTelemetry:', error);
    }
  }

  // Set up monitoring (always enabled but can be disabled)
  if (enableMonitoring) {
    // Monitoring is set up automatically via the service
    logger.debug('API monitoring enabled');
    
    // In development, log metrics periodically
    if (environment === 'development') {
      setInterval(() => {
        const metrics = monitoringService.getMetrics();
        if (metrics.requestCount > 0) {
          logger.debug('API Metrics:', {
            requests: metrics.requestCount,
            errors: metrics.errorCount,
            successRate: `${metrics.successRate.toFixed(2)}%`,
            avgResponseTime: `${metrics.averageResponseTime.toFixed(2)}ms`,
          });
        }
      }, 30000); // Log every 30 seconds
    }
  }

  // Add environment-specific configurations
  switch (environment) {
    case 'development':
      // More verbose logging, longer timeouts
      apiClient.setDefaultHeaders({
        'X-Environment': 'development',
      });
      break;
      
    case 'staging':
      apiClient.setDefaultHeaders({
        'X-Environment': 'staging',
      });
      break;
      
    case 'production':
      // Shorter timeouts, minimal logging
      apiClient.setDefaultHeaders({
        'X-Environment': 'production',
      });
      break;
  }

  logger.info('API layer initialized successfully');
};

/**
 * Get API health status
 */
export const getApiHealth = async () => {
  try {
    const health = await monitoringService.performHealthCheck();
    logger.debug('API health check:', health);
    return health;
  } catch (error) {
    logger.error('Health check failed:', error);
    return {
      status: 'unhealthy' as const,
      timestamp: new Date().toISOString(),
      responseTime: 0,
      checks: { api: false },
    };
  }
};

/**
 * Get API metrics for debugging/monitoring
 */
export const getApiMetrics = () => {
  const metrics = monitoringService.getMetrics();
  const summary = monitoringService.getPerformanceSummary();
  
  return {
    metrics,
    summary,
    exportedData: monitoringService.exportMetrics(),
  };
};

/**
 * Reset API metrics (useful for testing)
 */
export const resetApiMetrics = () => {
  monitoringService.resetMetrics();
  logger.info('API metrics reset');
}; 